/* eslint-disable max-classes-per-file */
class InvalidOptionError extends Error {}

const VALID_CHART_TYPES = /** @type {const} */ ["pie", "bar", "sentiment"];

/**
 * @typedef {object} ConfigStyle
 * @property {import("react").StyleHTMLAttributes<HTMLImageElement>} background
 * @property {import("react").StyleHTMLAttributes<HTMLDivElement>} blob
 * @property {import("react").StyleHTMLAttributes<HTMLDivElement>} persona
 * @property {import("react").StyleHTMLAttributes<HTMLHeadingElement>} text
 * @property {import("react").StyleHTMLAttributes<HTMLDivElement>} chart
 */
/**
 * @typedef {Object} DataVizConfigParams
 * @property {typeof VALID_CHART_TYPES[number] | null} chartType - type of chart to display
 * @property {string | {persona: import("./dataVizImage.js").DataVizImage, texture?: import("./dataVizImage.js").DataVizImage}} images - string to the image file to use
 * @property {null | number} timeout
 * @property {null | ConfigStyle} [style=null]
 */
export class DataVizConfig {
  /**
   * @param {DataVizConfigParams} args - object for named arguments
   */
  constructor({ chartType = null, images, timeout = null, style = null }) {
    if (chartType) {
      if (VALID_CHART_TYPES.includes(chartType)) {
        this._chartType = chartType;
      } else {
        throw new InvalidOptionError(
          `Chart type must be one of: ${VALID_CHART_TYPES.join(", ")}`
        );
      }
    } else {
      this._chartType = null;
    }

    this._images = images;
    if (typeof images === "string" || !images.texture) {
      this._images.texture = null;
    }

    this._timeout = timeout;

    if (style) {
      const validSectionNames = [
        "container",
        "blob",
        "persona",
        "text",
        "chart",
        "background",
      ];
      if (Object.keys(style).some((key) => !validSectionNames.includes(key))) {
        throw new InvalidOptionError(
          `Style must be an object with the following shape: { [sectionName]: { ...styleObj } }. Valid section names are: ${validSectionNames.join(
            ", "
          )}`
        );
      } else {
        this._style = style;
      }
    } else {
      this._style = null;
    }
  }

  get chartType() {
    return this._chartType;
  }

  /**
   * @param {typeof VALID_CHART_TYPES[number]} newChartType - type of chart to display
   */
  set chartType(newChartType) {
    if (VALID_CHART_TYPES.includes(newChartType)) {
      this._chartType = newChartType;
    } else {
      throw new InvalidOptionError(
        `Chart type must be one of: ${VALID_CHART_TYPES.join(", ")}`
      );
    }
  }

  get images() {
    return this._images;
  }

  set images(newImages) {
    this._images = newImages;
  }

  get timeout() {
    return this._timeout;
  }

  set timeout(newTimeout) {
    this._timeout = newTimeout;
  }

  get style() {
    return this._style;
  }

  set style(newStyle) {
    this._style = newStyle;
  }
}
